<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { langs, i18n, setLang, currentMeta, site } from '@/i18n'

const open = ref(false)
const wrap = ref<HTMLElement | null>(null)
const current = computed(() => currentMeta())

function toggle() {
  open.value = !open.value
}

function choose(key: string) {
  setLang(key as typeof i18n.lang)
  open.value = false
}

function onDocClick(e: MouseEvent) {
  if (wrap.value && !wrap.value.contains(e.target as Node)) open.value = false
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div ref="wrap" class="lang-switch">
    <button
      type="button"
      class="lang-trigger"
      :aria-label="current.name"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click.stop="toggle"
    >
      <span class="lang-flag" aria-hidden="true">{{ current.flag }}</span>
      <span class="lang-name">{{ current.name }}</span>
      <svg class="lang-chevron" :class="{ open }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>

    <Transition name="lang-pop">
      <ul v-show="open" class="lang-menu" role="listbox" :aria-label="site.ui.language">
        <li v-for="l in langs" :key="l.key" role="none">
          <button
            type="button"
            class="lang-option"
            :class="{ active: l.key === i18n.lang }"
            role="option"
            :aria-selected="l.key === i18n.lang"
            @click.stop="choose(l.key)"
          >
            <span class="lang-flag" aria-hidden="true">{{ l.flag }}</span>
            <span class="lang-opt-name">{{ l.name }}</span>
            <svg v-if="l.key === i18n.lang" class="lang-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<style scoped>
.lang-switch {
  position: relative;
}

.lang-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--u2-stroke);
  background: transparent;
  color: var(--u2-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.lang-trigger:hover {
  color: var(--u2-text);
  border-color: rgba(var(--u2-primary-rgb), 0.35);
  background: rgba(var(--u2-primary-rgb), 0.04);
}

.lang-flag {
  font-size: 17px;
  line-height: 1;
}

.lang-name {
  max-width: 7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lang-chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.2s ease;
}

.lang-chevron.open {
  transform: rotate(180deg);
}

.lang-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1200;
  min-width: 168px;
  margin: 0;
  padding: var(--space-2);
  list-style: none;
  background: var(--u2-elevated);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--u2-muted);
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  transition: color 0.15s ease, background 0.15s ease;
}

.lang-option:hover {
  color: var(--u2-text);
  background: rgba(var(--u2-primary-rgb), 0.06);
}

.lang-option.active {
  color: var(--u2-primary);
  font-weight: 600;
}

.lang-opt-name {
  flex: 1;
}

.lang-check {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.lang-pop-enter-active,
.lang-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.18s var(--ease-out-expo);
}

.lang-pop-enter-from,
.lang-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
</style>
